import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui-helpers";
import { Users, Phone, Video, Mail, MessageCircle, Eye, Clock, History, FilePlus, Trash2 } from "lucide-react";

const CHANNEL_ICONS = {
  "Walk-in": Users,
  Phone: Phone,
  "Online/Video": Video,
  Email: Mail,
  WhatsApp: MessageCircle,
};
const DEFAULT_CHANNELS = Object.keys(CHANNEL_ICONS);
const ICON = CHANNEL_ICONS;
const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const when = (iso) => {
  if (!iso) return "";
  const dt = new Date(iso);
  if (isNaN(dt)) return "";
  return dt.toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export function ClientInteractions({ clientId, canLog = true }) {
  const [items, setItems] = useState(null);
  const [channels, setChannels] = useState(DEFAULT_CHANNELS);
  const [channel, setChannel] = useState(DEFAULT_CHANNELS[0]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/clients/${clientId}/interactions`).then((r) => setItems(r.data)).catch(() => setItems([]));

  useEffect(() => {
    // Channel buttons come from Settings (admin can edit them in Developer mode).
    api.get("/settings").then((r) => {
      const list = r.data?.interaction_channels;
      if (Array.isArray(list) && list.length) { setChannels(list); setChannel(list[0]); }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    // Auto-log a quiet "opened" entry (server throttles to once / 30 min per user), then show the trail.
    api.post(`/clients/${clientId}/log-view`).catch(() => {}).finally(load);
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const log = async () => {
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/interactions`, { channel, note });
      setNote("");
      toast.success("Interaction logged");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6" data-testid="client-interactions">
      <div className="flex items-center gap-2 mb-1">
        <History className="h-4 w-4 text-primary" />
        <h3 className="font-head font-bold text-slate-900">Worked on by</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">Every time someone opens this profile it's logged here. Add a note whenever you help the client.</p>

      {canLog && (
        <div className="rounded-xl border border-slate-200 p-3 mb-4 space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {channels.map((name) => {
              const Icon = ICON[name] || History;
              return (
              <button
                key={name}
                type="button"
                data-testid={`interaction-channel-${slug(name)}`}
                onClick={() => setChannel(name)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${channel === name ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
              >
                <Icon className="h-3.5 w-3.5" /> {name}
              </button>
            );})}
          </div>
          <div className="flex items-center gap-2">
            <input
              data-testid="interaction-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !saving) log(); }}
              placeholder="Optional note — what did you help with?"
              className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-primary/30"
            />
            <button
              data-testid="interaction-log-btn"
              onClick={log}
              disabled={saving}
              className="shrink-0 bg-primary text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
            >
              {saving ? "Saving…" : "Log"}
            </button>
          </div>
        </div>
      )}

      {items === null ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-slate-400">No activity yet.</div>
      ) : (
        <ul className="space-y-3">
          {items.map((it) => {
            const viewed = it.kind === "viewed";
            const created = it.kind === "created";
            const deleted = it.kind === "deleted";
            const Icon = viewed ? Eye : created ? FilePlus : deleted ? Trash2 : (ICON[it.channel] || Clock);
            return (
              <li key={it.id} data-testid={`interaction-${it.id}`} className="flex items-start gap-3 text-sm">
                <span className={`mt-0.5 h-7 w-7 shrink-0 rounded-full flex items-center justify-center ${viewed ? "bg-slate-100 text-slate-400" : created ? "bg-emerald-50 text-emerald-600" : deleted ? "bg-rose-50 text-rose-600" : "bg-primary/10 text-primary"}`}>
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-slate-800">
                    <span className="font-semibold">{it.actor_name || "Someone"}</span>{" "}
                    {viewed ? <span className="text-slate-500">opened this profile</span>
                      : created ? <span className="text-slate-600">{it.note || "created this client"}</span>
                      : deleted ? <span className="text-rose-600">{it.note || "deleted a billing item"}</span>
                      : <span className="text-slate-500">· {it.channel}</span>}
                  </div>
                  {!viewed && !created && !deleted && it.note && <div className="text-slate-600 mt-0.5">{it.note}</div>}
                  <div className="text-xs text-slate-400 mt-0.5">{when(it.created_at)}</div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
